import { FormikValues } from 'formik'
import { get } from 'lodash'
import { ReactNode } from 'react'
import styled from 'styled-components'

import defaultProps from '@/components/Form/defaultProps'
import Field from '@/components/Form/Field'

type InputProps = {
  children?: ReactNode
  onChange?: (...args: any[]) => void
  className?: string
}

const StyledRadioInput = styled(Field)`
  flex-direction: row;
  flex-wrap: wrap;
  padding: 0;

  &.form__field--disabled {
    .field__input {
      border-color: var(--wg-gray-border);
      background-color: var(--wg-gray-border);
      cursor: not-allowed;
    }

    .field__label {
      cursor: not-allowed;
    }
  }

  &.form__field--error {
    .field__input {
      border: 1px solid var(--wg-red);
    }
  }

  /* TODO Iron out the Field stylings to be a bit more intuitive */
  .field__label {
    cursor: pointer;
    font-weight: normal;
    line-height: 1.25rem;
    margin-bottom: 0;
    order: 1;
  }

  .field__description {
    display: block;
    flex: 0 0 100%;
    margin: 4px 0 0 0;
    order: 3;
  }
`

const StyledInput = styled.input`
  appearance: none;
  background-color: var(--wg-background);
  border: 1px solid var(--wg-grey);
  border-radius: 50%;
  cursor: pointer;
  margin: 0 6px 0 0;
  order: 0;
  position: relative;
  transition: 0.2s all linear;
  height: 1.25rem;
  width: 1.25rem;

  &:checked {
    border: 6px solid var(--wg-green);
  }

  &:focus {
    outline: none;
  }
`

const InnerComponents: any = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  width: 100%;
  align-items: center;

  /* Only mess with the first input (as that's the radio button) any other inputs should be left alone */
  & > .field__input {
    appearance: none;
    background-color: var(--wg-background);
    border: 1px solid var(--wg-grey);
    border-radius: 50%;
    cursor: ${({ readOnly }: any) => (readOnly ? 'not-allowed' : 'pointer')};
    margin: 0 4px 0 0;
    order: 0;
    position: relative;
    transition: 0.2s all linear;
    height: 1.25rem;
    width: 1.25rem;

    &:checked {
      border: 6px solid var(--wg-green);
    }

    &:focus {
      outline: none;
    }
  }
`

/**
 * @example
 * <Radio.Input
 *   description={<FormattedMessage id="someDescription" defaultMessage="Some Radio" />}
 *   id="someField"
 *   label={<FormattedMessage id="someLabel" defaultMessage="Some Label" />}
 *   name="someField"
 *   required
 *   value="true"
 * />
 */
const RadioInput = ({ onChange = () => {}, className = '', children = null, ...props }: InputProps & FormikValues) => {
  return (
    <StyledRadioInput
      {...defaultProps}
      className={`form__radio-input ${className}`}
      {...props}
      render={({ formik, ...field }: any) => {
        const name = field.name
        const values = get(formik.values, name, null)
        return children ? (
          // Children check to preserve backward compatibility and lower initial migration cost
          // TODO Update `Field` to be grid-ready, accessible and cleaner
          <InnerComponents readOnly={props.readOnly}>
            <input
              {...field}
              checked={values === field.value}
              onBlur={formik.handleBlur}
              onChange={handleChange(formik, name, onChange)}
              type="radio"
            />
            {children}
          </InnerComponents>
        ) : (
          <StyledInput
            {...field}
            checked={values === field.value}
            onBlur={formik.handleBlur}
            onChange={handleChange(formik, name, onChange)}
            type="radio"
          />
        )
      }}
    />
  )
}

const handleChange = (formik: any, name: any, onChange: (E: any) => void) => {
  return (e: any) => {
    const resultingValue = e.target.checked ? e.target.value : null
    if (onChange) {
      onChange(e)
    }

    formik.setFieldValue(name, resultingValue)
  }
}

RadioInput.displayName = 'Input'

export default RadioInput
